<template>
    <div class="card mb-0 pt-0" v-if="learners">

        <p class="card-body mt-2" v-if="empty">
            <strong>{{ $t('dashboard.no-learner') }}</strong>
        </p>

        <div v-if="!empty">

            <el-table class="table-responsive table-flush"
                    header-row-class-name="thead-light"
                    :data="sortedLearners">

                <el-table-column width="72">
                    <template v-slot="{row}">
                        <a href="#" class="avatar rounded-circle" :class="{'disabled': isWithdrawed(row)}"
                            @click.prevent="openOrEditLearner(row)"
                            v-if="row.session && (row.state.published || $permissions.hasRight('dashboard.learners.write'))"
                        >
                            <img :src="row.avatar">
                        </a>
                        <span class="avatar rounded-circle" :class="{'disabled': isWithdrawed(row)}" v-else>
                            <img :src="row.avatar">
                        </span>
                    </template>
                </el-table-column>

                <el-table-column width="180" class-name="learner">
                    <div slot="header">
                        {{ $t('common.learner') }}
                    </div>
                    <template v-slot="{row}">
                        <div :class="{'disabled': isWithdrawed(row)}">
                            <h4 class="mb-0">
                                <a href="#" @click.prevent="openOrEditLearner(row)"
                                    v-if="row.session && (row.state.published || $permissions.hasRight('dashboard.learners.write'))"
                                >
                                    {{row.name}}
                                </a>
                                <span v-else>
                                    {{row.name}}
                                </span>
                            </h4>
                            <small v-if="row.owner">
                                {{ row.owner.name }}
                            </small>
                            <small v-if="row.entity">
                                / {{ row.entity.name }}
                            </small>
                        </div>
                    </template>
                </el-table-column>

                <el-table-column width="130">
                    <div slot="header">
                        {{ $t('common.status') }}
                    </div>
                    <template v-slot="{row}">

                        <badge type="success" v-if="row.state.published" class="mb-1">
                            <i class="fas fa-calendar-alt mr-1"></i>
                            {{ date(row) }}
                        </badge>

                    </template>
                </el-table-column>

                <el-table-column width="150">
                    <div slot="header">
                        {{ $t('common.info') }}
                    </div>
                   <template v-slot="{row}">

                        <small class="d-block"
                            v-if="row.state.published && position(row)"
                        >
                            <i class="fas fa-sync-alt mr-1"></i> {{ position(row) }}
                        </small>

                        <small class="d-block"
                            v-if="row.state.published && teammate(row) && $permissions.hasRight('dashboard.learners.read')"
                        >
                            <i class="fas fa-user-friends mr-1"></i> {{ teammate(row).name }}
                        </small>

                        <small v-if="row.state.published" class="d-block">
                            <i class="fas fa-pencil-alt mr-1"></i> {{ row.state.instructor_name }}
                        </small>

                        <small v-if="row.state.acknowledged" class="d-block">
                            <i class="fas fa-check mr-1"></i> {{ row.name }}
                        </small>

                    </template>
                </el-table-column>
               
                <el-table-column min-width="300">
                    <div slot="header">
                        {{ $t('dashboard.comment') }}
                    </div>
                    <template v-slot="{row}">

                        <!-- Keep it on the same line -->
                        <p class="comment" v-if="row.state.published">{{ row.state.comment }}</p>

                    </template>
                </el-table-column>
               
                <el-table-column v-for="(competency, index) in competencies" :key="index"
                    align="center" header-align="center"
                    class-name="rating" width="50"
                >
                    <div slot="header">
                        <div class="vertical pt-2 pb-2">{{ competencyName(competency) }}</div>
                    </div>

                    <template v-slot="{row}">
                        <rating v-if="row.state.published && row.state.competencies && row.state.competencies[simulationRole]"
                            :name="competencyName(competency)"
                            :description="competencyDescription(competency.id)"
                            :value="row.state.competencies[simulationRole][competency.id]"
                            :options="ratingOptions"
                            :comment="competencyComment(row.state, simulationRole, competency.id)"
                        >
                        </rating>
                    </template>

                </el-table-column>
                
                <el-table-column width="10">
                </el-table-column>

            </el-table>
        </div>
    </div>
</template>

<script>
    import moment from 'moment'
    import Rating from '../learner/Rating'

    export default {

        components: {
            Rating
        },

        props: {

            num: {
                type: Number,
            },

            registration: {
                type: Object,
            },

            simulation: {
                type: Object,
            },

            learners: {
                type: Array,
            },

            ratingOptions: {
                type: Array,
            },
        },
        
        data() {
            return {
                moment: moment,
            }
        },

        computed: {
            empty() {
                return this.learners.length == 0
            },

            sortedLearners() {
                return [...this.learners].sort((a, b) => a.name.toUpperCase() > b.name.toUpperCase() ? 1 : -1)
            },

            simulationRole() {
                return this.registration.degree['simulation_role_' + this.num]
            },

            competencies() {
                const competenciesObject = {};
                if (this.simulation['grid' + this.num]) {
                    this.simulation['grid' + this.num].competencies.forEach(competency => {
                        competenciesObject[competency.id] = competency
                    });
                }
                return Object.values(competenciesObject).sort((a, b) => a.position - b.position)
            }
        },

        methods: {

            isWithdrawed(learner) {
                return !learner.pivot.active
            },

            competencyName(competency) {
                const nameProp = 'name_' + this.registration.degree.lang
                return competency[nameProp]
            },

            competencyDescription(competencyId) {
                if (!this.simulation['grid' + this.num]) {
                    return ''
                }
                const competency = this.simulation['grid' + this.num].competencies.find(competency => competency.id == competencyId)
                if (!competency) {
                    return ''
                }
                return competency.pivot.description
            },

            competencyComment(state, simulationRole, id) {
                return state.competency_comments && state.competency_comments[simulationRole]
                    ? state.competency_comments[simulationRole][id]
                    : null
            },

            date(learner) {
                if (learner.session && learner.session.started_at) {
                    return this.moment(learner.session.started_at).format('YYYY/MM/DD')
                } else {
                    return this.moment(learner.state.date).format('YYYY/MM/DD')
                }
            },
            
            teammate(learner) {
                if (!learner.session) {
                    return false
                }
                if (learner.id == learner.session.learner1_id && learner.session.learner2) {
                    return learner.session.learner2
                }
                if (learner.id == learner.session.learner2_id && learner.session.learner1) {
                    return learner.session.learner1
                }
                if (learner.session.teammate) {
                    return learner.session.teammate
                }
                return null
            },

            position(learner) {
                if (!learner.session) {
                    return false
                }
                if (learner.id == learner.session.learner1_id) {
                    let position = this.registration.degree.simulation_role_1_name[this.registration.degree.lang];
                    if (this.registration.degree.simulation_role_2) {
                        position += ' / ' + this.registration.degree.simulation_role_2_name[this.registration.degree.lang];
                    }
                    return position
                }
                if (learner.id == learner.session.learner2_id) {
                    let position = this.registration.degree.simulation_role_2_name[this.registration.degree.lang];
                    if (this.registration.degree.simulation_role_1) {
                        position += ' / ' + this.registration.degree.simulation_role_1_name[this.registration.degree.lang];
                    }
                    return position
                }
                return false
            },
            
            openOrEditLearner(learner) {
                if (learner.state.published) {
                    this.openLearner(learner)
                } else {
                    this.editLearner(learner)
                }
            },

            openLearner(learner) {
                this.$router.push({ name: 'dashboard-learner-simulation',
                    params: { 
                        learnerId: learner.id,
                        registrationId: this.registration.id,
                        simulationId: this.$route.params.simulationId,
                    }
                })
            },

            editLearner(learner) {
                this.$router.push({ name: 'dashboard-learner-simulation-edit',
                    params: { 
                        learnerId: learner.id,
                        registrationId: this.registration.id,
                        simulationId: this.$route.params.simulationId,
                    }
                })
            },
        }
    }
</script>

<style scoped>

    .card >>> td {
        padding: 1rem 0 1rem 1rem;
        font-size: 0.8rem;
    }
    .card >>> th.rating {
        padding-left: 1rem;
        padding-right: 0;
    }
    .card >>> td.rating {
        padding-left: 0;
        padding-right: 0;
    }
    .card >>> td.learner {
        padding-left: 0.5rem;
    }
    .card >>> .vertical {
        writing-mode: vertical-rl;
        transform: rotate(180deg);
    }
    .card >>> .comment {
        white-space: pre-wrap;
        font-size: 100%;
    }
    .badge.badge-success {
        background-color: #1aae6f;
        color: #fff;
        font-size: 0.7rem;
    }
    .disabled, .disabled h4 {
        color: #bbb !important;
    }
    .disabled img {
        opacity: 0.5;
    }

</style>